import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Check, CheckCircle, ExternalLink, Map as MapIcon, Pencil, Shield, Trash2, UserPlus, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { SignUp } from './types'
import type { MatchTeam } from '@/lib/factions'
import FactionIcon from '@/components/matches/FactionIcon'
import EmojiGlyph from '@/components/ui/EmojiGlyph'
import { roleOf, typeOf, useRosterCatalog } from '@/lib/rosterCatalog'
import { useAuth } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'

// Fixed display order: active classes → bench → tentative → absence
const CLASS_ORDER = [
  'Inf', 'Tank', 'Recon', 'Artillery', 'Arty', 'Comm', 'Streamer',
  'infantry', 'armour', 'recon', 'artillery', 'commander', 'streamer', 'tank',
  'Bench', 'Tentative', 'Absence', 'Late',
]
const CLASS_PRIORITY = Object.fromEntries(CLASS_ORDER.map((c, i) => [c, i]))
const ABSENCE_CLASSES = new Set(['Absence', 'Late'])

const DAY   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
const MONTH = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function formatClock(ts: number) {
  return new Date(ts * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

function formatDay(ts: number) {
  const d = new Date(ts * 1000)
  return `${DAY[d.getDay()]}, ${d.getDate()} ${MONTH[d.getMonth()]}`
}

function formatDate(ts: number) {
  const d = new Date(ts * 1000)
  return `${DAY[d.getDay()]}, ${d.getDate()} ${MONTH[d.getMonth()]} · ${d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}`
}

function parseDiscordTimestamps(text: string): string {
  return text.replace(/<t:(\d+)(?::[A-Za-z])?>/g, (_, ts) => {
    const d = new Date(Number(ts) * 1000)
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  })
}

function DiscordEmote({ emoteId, name, size = 16 }: { emoteId?: string | null; name?: string; size?: number }) {
  if (!emoteId || emoteId === 'default') return null
  return (
    <img
      src={`https://cdn.discordapp.com/emojis/${emoteId}.webp?size=32`}
      style={{ width: size, height: size }}
      className="object-contain shrink-0"
      onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
      title={name}
    />
  )
}

/** A class's icon: its Raid Helper emote if it has one, else its own emoji.
 *
 * Dashboard positions store the emoji as one string either way — a unicode
 * character or Discord's `<:name:id>` — so EmojiGlyph decides which it is. */
function ClassGlyph({ cls, size = 14 }: { cls: RhClass; size?: number }) {
  if (cls.emoteId && cls.emoteId !== 'default') {
    return <DiscordEmote emoteId={cls.emoteId} name={cls.name} size={size} />
  }
  return <EmojiGlyph emoji={cls.emoji} size={size} />
}

// Always shows best available icon: spec > class > emoji fallback
export function PlayerIcon({ su, emoji }: {
  su: Pick<SignUp, 'class_name' | 'class_emote_id' | 'spec_emote_id'>
  /** From the editable class table; wins over the built-in fallbacks below. */
  emoji?: string
}) {
  const emoteId = (su.spec_emote_id && su.spec_emote_id !== 'default')
    ? su.spec_emote_id
    : (su.class_emote_id && su.class_emote_id !== 'default')
      ? su.class_emote_id
      : null
  const fallbacks: Record<string, string> = {
    Inf:'🪖', Tank:'🛡️', Recon:'🎯', Comm:'📻', Arty:'💥', Artillery:'💥', Streamer:'🎥',
    Bench:'🪑', Absence:'❌', Late:'⏰', Tentative:'❓',
    infantry:'🪖', armour:'🛡️', recon:'🎯', commander:'📻', artillery:'💥', tank:'🛡️',
  }
  if (emoteId) {
    return (
      <img
        src={`https://cdn.discordapp.com/emojis/${emoteId}.webp?size=32`}
        className="h-4 w-4 object-contain shrink-0"
        onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
      />
    )
  }
  const glyph = emoji || fallbacks[su.class_name]
  return <EmojiGlyph emoji={glyph} size={13} />
}

export function Avatar({ su, px }: { su: Pick<SignUp, 'name' | 'avatar_url'>, px: number }) {
  const style = { width: px, height: px }
  return su.avatar_url
    ? <img src={su.avatar_url} style={style} className="rounded-full object-cover shrink-0" />
    : <div style={style} className="rounded-full bg-muted shrink-0 flex items-center justify-center text-[9px] font-bold">{su.name[0]?.toUpperCase()}</div>
}

interface Props {
  title: string
  startTime?: number | null
  /** Optional briefing before the match; shown next to the kick-off time. */
  briefingTime?: number | null
  /** Dashboard match: list briefing, map and teams even when empty, as
   *  "not decided yet". Raid Helper events never had these fields. */
  markUnset?: boolean
  description?: string
  eventId?: string
  signupBase?: string
  isExternal?: boolean
  discordUrl?: string
  signUps: SignUp[]
  onConfigureSquads?: () => void
  configLabel?: string
  onRefresh?: () => void
  onEditMatch?: () => void
  onRemoveMatch?: () => void
  /** Confirmation text for the remove button; falls back to a plain Yes/No. */
  removeLabel?: string
  mapName?: string
  /** Dashboard matches only; Raid Helper events have no side list. */
  teams?: MatchTeam[]
  onSignUpChange?: (su: SignUp | null) => void
  /** Step 3 card (the match strategy), drawn next to sign-up and position. */
  strategySlot?: ReactNode
}

/** A sub-class. Raid Helper sends `emoteId`; dashboard sub-classes an `emoji` string. */
interface RhSpec  { name: string; emoteId?: string; emoji?: string }

export function SpecGlyph({ spec, size = 14 }: { spec: RhSpec; size?: number }) {
  if (spec.emoteId && spec.emoteId !== 'default') {
    return <DiscordEmote emoteId={spec.emoteId} name={spec.name} size={size} />
  }
  return spec.emoji ? <EmojiGlyph emoji={spec.emoji} size={size} /> : null
}
/**
 * A sign-up class. Raid Helper supplies `emoteId` (a Discord custom emote) and
 * types every non-playing class 'default'; dashboard matches supply a plain
 * `emoji` and type theirs 'extra'. Both shapes flow through here.
 */
export interface RhClass {
  name: string
  type: string
  emoteId?: string
  emoji?: string
  kind?: string
  specs: RhSpec[]
  /** Dashboard matches: the sub-class this user picked last time, pre-selected. */
  last_spec?: string | null
}

// Raid Helper marks dozens of things 'default'; only these are worth offering.
const RH_EXTRA_CLASSES = new Set(['Bench', 'Tentative', 'Absence'])

const NON_ACTIVE_CLASSES = new Set(['Bench', 'Tentative', 'Absence', 'Late'])

// ── Sign-up section ────────────────────────────────────────────────────────────

export function SignUpSection({ eventId, signupBase, classes, classesLoading, currentSignUp, onLocalSignUpChange, onRefresh }: {
  eventId: string
  signupBase: string
  classes: RhClass[]
  classesLoading: boolean
  currentSignUp: SignUp | undefined
  onLocalSignUpChange?: (su: SignUp | null) => void
  onRefresh?: () => void
}) {
  const { user } = useAuth()
  const { T } = useLanguage()
  const [selectedClass, setSelectedClass]   = useState('')
  const [selectedSpec, setSelectedSpec]     = useState('')
  const [selectedSpecEmote, setSelectedSpecEmote] = useState('')
  const [busy, setBusy]                     = useState(false)
  const [myClass, setMyClass]               = useState<string | null>(currentSignUp?.class_name ?? null)
  const [mySpec, setMySpec]                 = useState<string | null>(currentSignUp?.spec_name ?? null)
  const [mySpecEmote, setMySpecEmote]       = useState<string | null>(currentSignUp?.spec_emote_id ?? null)
  const [myClassEmote, setMyClassEmote]     = useState<string | null>(currentSignUp?.class_emote_id ?? null)
  // Signed up and picking another position: a second sign-up moves the
  // player (the row is keyed on match + user), so there is no need to cancel.
  const [changing, setChanging]             = useState(false)

  // Sync when currentSignUp prop updates (e.g. after parent refresh)
  useEffect(() => {
    if (currentSignUp) {
      setMyClass(currentSignUp.class_name)
      setMyClassEmote(currentSignUp.class_emote_id ?? null)
      setMySpec(currentSignUp.spec_name ?? null)
      setMySpecEmote(currentSignUp.spec_emote_id ?? null)
    }
  }, [currentSignUp])

  if (!user) return null

  const primaryClasses = classes.filter(c => c.type === 'primary')
  const extraClasses   = classes.filter(
    c => c.type === 'extra' || (c.type === 'default' && RH_EXTRA_CLASSES.has(c.name)),
  )
  const allOptions     = [...primaryClasses, ...extraClasses]

  const selectedClassObj = allOptions.find(c => c.name === selectedClass)
  const specs: RhSpec[]  = selectedClassObj?.specs ?? []
  // The sub-class is optional: a class with sub-classes can be signed up for
  // without one. Clicking the picked sub-class again clears it.
  const canSignUp        = !!selectedClass

  /** Picks a class and pre-selects a sub-class: the given one (changing an
   *  existing sign-up keeps its own), else the one remembered for this class. */
  const handleSelectClass = (cls: string, prefer?: string | null) => {
    const options = allOptions.find(c => c.name === cls)?.specs ?? []
    const spec = options.find(s => s.name === prefer)
      ?? options.find(s => s.name === allOptions.find(c => c.name === cls)?.last_spec)
    setSelectedClass(cls)
    setSelectedSpec(spec?.name ?? '')
    setSelectedSpecEmote(spec?.emoteId ?? '')
  }

  const handleSelectSpec = (name: string, emoteId?: string) => {
    const clear = selectedSpec === name
    setSelectedSpec(clear ? '' : name)
    setSelectedSpecEmote(clear ? '' : emoteId ?? '')
  }

  const handleSignUp = async () => {
    if (!canSignUp) return
    setBusy(true)
    try {
      const res = await fetch(`/api/${signupBase}/${eventId}/signup`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ class_name: selectedClass, spec_name: selectedSpec || undefined }),
      })
      if (res.ok) {
        const classEmote = selectedClassObj?.emoteId ?? null
        setChanging(false)
        setMyClass(selectedClass)
        setMySpec(selectedSpec || null)
        setMySpecEmote(selectedSpecEmote || null)
        setMyClassEmote(classEmote)
        setSelectedClass(''); setSelectedSpec(''); setSelectedSpecEmote('')
        onLocalSignUpChange?.({
          user_id: user.id,
          name: user.name,
          class_name: selectedClass,
          class_emote_id: classEmote,
          spec_name: selectedSpec || null,
          spec_emote_id: selectedSpecEmote || null,
          // Dashboard classes carry their kind; Raid Helper ones only a name.
          is_active: selectedClassObj?.kind
            ? selectedClassObj.kind === 'primary'
            : !NON_ACTIVE_CLASSES.has(selectedClass),
          is_bench: selectedClassObj?.kind ? selectedClassObj.kind === 'bench' : undefined,
          avatar_url: user.avatar || null,
          in_roster: false,
        })
        // Refresh real data after a short delay to let backend cache update
        if (onRefresh) setTimeout(onRefresh, 1500)
      }
    } finally { setBusy(false) }
  }

  const handleCancel = async () => {
    setBusy(true)
    try {
      const res = await fetch(`/api/${signupBase}/${eventId}/signup`, { method: 'DELETE', credentials: 'include' })
      if (res.ok) {
        setMyClass(null); setMySpec(null); setMySpecEmote(null); setMyClassEmote(null)
        setSelectedClass(''); setSelectedSpec(''); setSelectedSpecEmote('')
        onLocalSignUpChange?.(null)
        // Refresh real data after a short delay to let backend cache update
        if (onRefresh) setTimeout(onRefresh, 1500)
      }
    } finally { setBusy(false) }
  }

  // ── Signed up state ──────────────────────────────────────────────────────────
  if (myClass && !changing) {
    return (
      <div className="space-y-2.5">
        <div className="flex items-center gap-2.5 px-3 py-2 rounded-sm bg-green-500/12 border border-green-500/25">
          <div className="h-5 w-5 rounded-full bg-green-500/25 flex items-center justify-center shrink-0">
            <Check className="h-3 w-3 text-green-400" />
          </div>
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <DiscordEmote emoteId={myClassEmote} name={myClass} size={14} />
            <span className="text-sm font-bold text-foreground">{myClass}</span>
            {mySpec && (
              <>
                <span className="text-muted-foreground/40 text-xs">/</span>
                {mySpecEmote
                  ? <DiscordEmote emoteId={mySpecEmote} name={mySpec} size={14} />
                  : <SpecGlyph spec={allOptions.find(c => c.name === myClass)?.specs.find(sp => sp.name === mySpec) ?? { name: mySpec }} size={14} />}
                <span className="text-sm font-semibold text-foreground">{mySpec}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setChanging(true); handleSelectClass(myClass, mySpec) }}
            disabled={busy}
            className="flex-1 px-3 py-1.5 rounded-md text-[11px] font-medium border border-white/10 text-muted-foreground bg-white/5 hover:bg-white/10 hover:text-foreground transition-all disabled:opacity-40"
          >
            {T.eventDetail.changeSignUp}
          </button>
          <button
            onClick={handleCancel}
            disabled={busy}
            className="flex-1 px-3 py-1.5 rounded-md text-[11px] font-medium border border-red-500/25 text-red-400/70 bg-red-500/8 hover:bg-red-500/15 hover:text-red-400 transition-all disabled:opacity-40"
          >
            {T.eventDetail.cancelSignUp}
          </button>
        </div>
      </div>
    )
  }

  // ── Not signed up (class / spec picker) ────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* Status */}
      {changing ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground/70">{T.eventDetail.changeSignUpHint}</p>
          <button onClick={() => { setChanging(false); handleSelectClass('') }}
            className="text-[11px] text-muted-foreground/60 underline-offset-2 hover:text-foreground hover:underline">
            {T.eventDetail.cancelAction}
          </button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground/70">{T.eventDetail.notSignedUp}</p>
      )}

      {/* Class picker */}
      <div className="space-y-1.5">
        <p className="text-[10px] font-semibold tracking-widest text-muted-foreground/40 uppercase">
          {T.eventDetail.chooseClass}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {classesLoading ? (
            [72, 56, 64, 80, 60, 72].map((w, i) => (
              <div key={i} style={{ width: w }} className="h-[26px] rounded-md bg-white/5 animate-pulse" />
            ))
          ) : allOptions.map(c => (
            <button
              key={c.name}
              onClick={() => handleSelectClass(c.name)}
              disabled={busy}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-all ${
                selectedClass === c.name
                  ? 'bg-primary/20 border-primary/50 text-primary'
                  : 'bg-white/5 border-white/10 text-muted-foreground hover:bg-white/10 hover:text-foreground'
              }`}
            >
              <ClassGlyph cls={c} size={13} />
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* Spec picker (only when class with specs selected) */}
      {selectedClass && specs.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold tracking-widest text-muted-foreground/40 uppercase">
            {T.eventDetail.chooseSpec}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {specs.map(s => (
              <button
                key={s.name}
                onClick={() => handleSelectSpec(s.name, s.emoteId)}
                disabled={busy}
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-all ${
                  selectedSpec === s.name
                    ? 'bg-primary/20 border-primary/50 text-primary'
                    : 'bg-white/5 border-white/10 text-muted-foreground hover:bg-white/10 hover:text-foreground'
                }`}
              >
                <SpecGlyph spec={s} size={13} />
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sign Up button */}
      <button
        onClick={handleSignUp}
        disabled={!canSignUp || busy}
        className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium border border-primary/30 text-primary/80 bg-primary/8 hover:bg-primary/15 hover:text-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <UserPlus className="h-3 w-3" />
        {changing ? T.eventDetail.changeSignUpSave : T.eventDetail.signUp}
      </button>
    </div>
  )
}

function ConfirmDot({ status }: { status?: string }) {
  if (status === 'yes') return <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" title="Confirmed" />
  if (status === 'no') return <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" title="Denied" />
  return <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse shrink-0" title="Pending" />
}

interface SquadSlot {
  id?: string
  role: string
  userId?: string
  playerName?: string
}

export interface SquadData {
  id: string
  name: string
  type: string
  icon?: string
  slots: SquadSlot[]
}

export function SquadDisplaySection({
  squads, confirmations, signUps, layoutLabel = 'SQUAD LAYOUT',
}: {
  squads: SquadData[]
  confirmations: Record<string, string>
  signUps: SignUp[]
  layoutLabel?: string
}) {
  const { T } = useLanguage()
  const catalog = useRosterCatalog()
  if (squads.length === 0) return null

  // Build avatar + name lookup from sign-ups
  const avatarMap: Record<string, string> = {}
  const nameMap: Record<string, string> = {}
  for (const su of signUps) {
    if (su.avatar_url) avatarMap[su.user_id] = su.avatar_url
    if (su.name) nameMap[su.user_id] = su.name
  }

  return (
    <div>
      <p className="text-[10px] font-semibold tracking-widest text-muted-foreground/60 mb-3 flex items-center gap-1.5">
        <Shield className="h-3 w-3" />
        {layoutLabel}
      </p>
      <div className="flex flex-wrap gap-2">
        {squads.map(sq => {
          const filled = sq.slots.filter(s => s.userId)
          return (
            <div key={sq.id} className="rounded-sm bg-white/[0.035] border border-white/10 p-2.5 min-w-[180px] max-w-[220px] flex-shrink-0">
              <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-white/10">
                {sq.type && <EmojiGlyph emoji={typeOf(catalog, sq.type)?.emoji} size={14} />}
                <span className="text-[10px] font-semibold tracking-wider text-muted-foreground/60">
                  {sq.name}
                </span>
                <span className="text-[9px] text-muted-foreground/40 ml-auto">
                  {filled.length}/{sq.slots.length}
                </span>
              </div>
              <div className="space-y-0.5">
                {sq.slots.map((slot, i) => {
                  const role = roleOf(catalog, slot.role)
                  const label = role?.name || slot.role
                  if (!slot.userId) {
                    return (
                      <div key={i} className="flex items-center gap-1.5 px-1.5 py-1 rounded text-[10px] text-muted-foreground/25">
                        <span className="inline-flex opacity-30"><EmojiGlyph emoji={role?.emoji} size={12} /></span>
                        <span className="italic">{T.eventDetail.emptySlot} ({label})</span>
                      </div>
                    )
                  }
                  const avatarUrl = avatarMap[slot.userId]
                  return (
                    <div key={i} className="flex items-center gap-1.5 px-1.5 py-1 rounded bg-white/[0.035]">
                      <ConfirmDot status={confirmations[slot.userId]} />
                      <EmojiGlyph emoji={role?.emoji} size={12} />
                      {avatarUrl ? (
                        <img src={avatarUrl} className="h-4 w-4 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="h-4 w-4 rounded-full bg-muted shrink-0 flex items-center justify-center text-[7px] font-bold">
                          {(slot.playerName || '?')[0]?.toUpperCase()}
                        </div>
                      )}
                      <span className="text-[11px] truncate flex-1 min-w-0">{slot.playerName || (slot.userId && nameMap[slot.userId]) || slot.userId}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Position card ───────────────────────────────────────────────────────────────

export function PositionBody({
  eventId, signupBase, squadLayout, bench, confirmations, userId, onConfirmChange,
}: {
  eventId: string
  signupBase: string
  squadLayout: SquadData[] | null
  /** Published bench (user ids). They are asked to confirm too. */
  bench?: string[]
  confirmations: Record<string, string>
  userId?: string
  onConfirmChange: () => void
}) {
  const { T } = useLanguage()
  const [confirming, setConfirming] = useState(false)
  // Declining opens a reason box first; "no" never goes out without one.
  const [declining, setDeclining] = useState(false)
  const [reason, setReason] = useState('')
  const [sentReason, setSentReason] = useState('')
  const [reasonError, setReasonError] = useState(false)

  // Find user's position in the squad layout
  let mySquad: string | null = null
  let myRole: string | null = null
  if (squadLayout && userId) {
    for (const sq of squadLayout) {
      for (const slot of sq.slots) {
        if (slot.userId === userId) {
          mySquad = sq.name
          myRole = slot.role
          break
        }
      }
      if (mySquad) break
    }
  }
  const onBench = !mySquad && !!userId && !!bench?.includes(userId)

  const myStatus = userId ? confirmations[userId] : undefined
  const hasLayout = squadLayout !== null && squadLayout.length > 0

  const send = async (status: 'yes' | 'no', why = '') => {
    if (!userId) return false
    setConfirming(true)
    try {
      const res = await fetch(`/api/${signupBase}/${eventId}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId, status, reason: why }),
      })
      onConfirmChange()
      return res.ok
    } finally {
      setConfirming(false)
    }
  }

  const submitDecline = async () => {
    const why = reason.trim()
    if (why.length < DECLINE_REASON_MIN) { setReasonError(true); return }
    if (await send('no', why)) {
      setSentReason(why)
      setDeclining(false)
      setReason('')
    }
  }

  const catalog = useRosterCatalog()
  const myRoleDef = myRole ? roleOf(catalog, myRole) : undefined
  const roleIcon = myRoleDef?.emoji || null
  // The role's name as Settings spells it — upper-casing it here would apply
  // Turkish rules to an English name ("Infantry" → "İNFANTRY").
  const roleLabel = myRole ? (myRoleDef?.name || myRole) : null

  return (
    <>
        {!hasLayout ? (
          <p className="text-sm text-muted-foreground/50 italic">{T.eventDetail.rosterNotPosted}</p>
        ) : !mySquad && !onBench ? (
          <p className="text-sm text-muted-foreground/50 italic">{T.eventDetail.notInRoster}</p>
        ) : (
          <div className="space-y-3">
            {/* Position info */}
            <div className="flex items-center gap-2.5 px-3 py-2 rounded-sm bg-white/5 border border-white/10">
              {roleIcon && <EmojiGlyph emoji={roleIcon} size={20} />}
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-bold text-foreground">{onBench ? T.eventDetail.benchPosition : mySquad}</span>
                <span className="text-xs text-muted-foreground">{onBench ? T.eventDetail.benchPositionHint : roleLabel}</span>
              </div>
            </div>

            {/* Confirmation status + buttons */}
            {myStatus === 'yes' ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-sm bg-green-500/12 border border-green-500/25">
                <CheckCircle className="h-4 w-4 text-green-400 shrink-0" />
                <span className="text-sm font-medium text-green-400">{T.eventDetail.positionConfirmed}</span>
              </div>
            ) : myStatus === 'no' ? (
              <div className="rounded-sm bg-red-500/12 border border-red-500/25 px-3 py-2">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-400 shrink-0" />
                  <span className="text-sm font-medium text-red-400">{T.eventDetail.positionDeclined}</span>
                </div>
                {sentReason && (
                  <p className="mt-1 pl-6 text-xs text-red-300/80">{T.eventDetail.declineReasonYours.replace('{reason}', sentReason)}</p>
                )}
              </div>
            ) : null}

            {declining ? (
              <div className="space-y-2">
                <label className="block text-xs text-muted-foreground" htmlFor="decline-reason">
                  {T.eventDetail.declineReasonLabel}
                </label>
                <textarea
                  id="decline-reason"
                  autoFocus
                  rows={4}
                  maxLength={DECLINE_REASON_MAX}
                  value={reason}
                  onChange={e => { setReason(e.target.value); setReasonError(false) }}
                  placeholder={T.eventDetail.declineReasonPlaceholder}
                  className={`w-full resize-none rounded-sm border bg-background/40 px-2.5 py-1.5 text-sm outline-none placeholder:text-muted-foreground/40 focus:border-primary ${
                    reasonError ? 'border-destructive/70' : 'border-white/10'
                  }`}
                />
                {reasonError && <p className="text-[11px] text-destructive">{T.eventDetail.declineReasonTooShort}</p>}
                <div className="flex gap-2">
                  <button
                    disabled={confirming}
                    onClick={() => { setDeclining(false); setReason(''); setReasonError(false) }}
                    className="flex-1 px-3 py-1.5 rounded-md text-xs font-medium border border-white/10 text-muted-foreground bg-white/5 hover:bg-white/10 hover:text-foreground transition-all"
                  >
                    {T.eventDetail.cancelAction}
                  </button>
                  <button
                    disabled={confirming}
                    onClick={submitDecline}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-red-500/40 text-red-300 bg-red-500/15 hover:bg-red-500/25 transition-all disabled:opacity-40"
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    {T.eventDetail.declineSubmit}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  disabled={confirming}
                  onClick={() => { setSentReason(''); send('yes') }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    myStatus === 'yes'
                      ? 'border border-green-500/50 text-green-300 bg-green-500/25 shadow-[0_0_8px_rgba(34,197,94,0.15)] cursor-default'
                      : 'border border-green-500/20 text-green-400/70 bg-green-500/8 hover:bg-green-500/15 hover:text-green-400 cursor-pointer'
                  }`}
                >
                  <CheckCircle className="h-3.5 w-3.5" />
                  {T.eventDetail.confirm}
                </button>
                <button
                  disabled={confirming}
                  onClick={() => setDeclining(true)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    myStatus === 'no'
                      ? 'border border-red-500/50 text-red-300 bg-red-500/25 shadow-[0_0_8px_rgba(239,68,68,0.15)]'
                      : 'border border-red-500/20 text-red-400/70 bg-red-500/8 hover:bg-red-500/15 hover:text-red-400 cursor-pointer'
                  }`}
                >
                  <XCircle className="h-3.5 w-3.5" />
                  {T.eventDetail.decline}
                </button>
              </div>
            )}
          </div>
        )}
    </>
  )
}

// Same bounds as the backend (events/router.py) and the Discord form.
const DECLINE_REASON_MIN = 3
const DECLINE_REASON_MAX = 300

function PositionCard(props: Parameters<typeof PositionBody>[0]) {
  const { T } = useLanguage()
  return (
    <div className="max-w-sm flex-1 min-w-[260px]">
      <div className="bg-white/[0.035] border border-white/10 rounded-sm px-4 py-3 space-y-3 h-full">
        <p className="text-base font-bold tracking-wide text-foreground">{T.eventDetail.yourPosition}</p>
        <PositionBody {...props} />
      </div>
    </div>
  )
}

// ── Sign-ups grouped by position ─────────────────────────────────────────────

/** Every sign-up in a column per position, in the order Settings lists them. */
export function SignUpsByClass({ signUps, classes, classesLoading, isLocalMatch }: {
  signUps: SignUp[]
  classes: RhClass[]
  classesLoading: boolean
  isLocalMatch: boolean
}) {
  const { T } = useLanguage()
  const classRank  = new Map(classes.map((c, i) => [c.name, i]))
  const classEmoji = new Map(classes.map(c => [c.name, c.emoji ?? '']))
  const classGroups: Record<string, SignUp[]> = {}
  for (const su of signUps) (classGroups[su.class_name] ??= []).push(su)
  const classOrder = Object.keys(classGroups).sort((a, b) => {
    const rank = (c: string) => classRank.get(c) ?? CLASS_PRIORITY[c] ?? 98
    return rank(a) - rank(b)
  })

  return (
    <div className="flex gap-5 overflow-x-auto pb-2 items-start">
      {classOrder.map(cls => {
        const players   = classGroups[cls]
        const isInf     = cls === 'Inf' || cls === 'infantry'
        const isAbsent  = ABSENCE_CLASSES.has(cls)
        const isBench   = cls === 'Bench'
        const isTent    = cls === 'Tentative'
        const isStream  = cls === 'Streamer' || cls === 'streamer'
        // A dashboard match keeps the position *name* a sign-up was made
        // with; one since removed in Settings sorts last and is not
        // counted, so say so rather than draw it like a live column.
        const isRemoved = isLocalMatch && !classesLoading && classes.length > 0 && !classRank.has(cls)

        const headerColor = isAbsent ? 'text-red-400/60'
          : isTent    ? 'text-live/60'
          : isStream  ? 'text-violet-400/60'
          : isBench   ? 'text-muted-foreground/35'
          : 'text-muted-foreground/50'

        const cardClass = isAbsent
          ? 'flex items-center gap-1.5 rounded px-2 py-1.5 bg-red-500/8 border border-red-500/20 min-w-0'
          : isTent
            ? 'flex items-center gap-1.5 rounded px-2 py-1.5 bg-live/8 border border-live/20 min-w-0'
            : isStream
              ? 'flex items-center gap-1.5 rounded px-2 py-1.5 bg-violet-500/8 border border-violet-500/20 min-w-0'
              : isBench
                ? 'flex items-center gap-1.5 rounded px-2 py-1.5 bg-white/[0.035] border border-white/6 min-w-0'
                : 'flex items-center gap-1.5 rounded px-2 py-1.5 bg-white/5 border border-white/10 min-w-0'

        const nameClass = isAbsent  ? 'text-[11px] truncate flex-1 min-w-0 text-red-400/70'
          : isTent   ? 'text-[11px] truncate flex-1 min-w-0 text-live/70'
          : isStream ? 'text-[11px] truncate flex-1 min-w-0 text-violet-400/80'
          : 'text-[11px] truncate flex-1 min-w-0'

        const borderColor = isAbsent ? 'border-red-500/20' : isTent ? 'border-live/20' : isStream ? 'border-violet-500/20' : 'border-white/10'

        return (
          <div key={cls} className={`${isInf ? 'min-w-[300px]' : 'min-w-[145px]'} flex-shrink-0 ${isRemoved ? 'opacity-50' : ''}`}
            title={isRemoved ? T.eventDetail.removedClassHint : undefined}>
            <div className={`flex items-center gap-1.5 mb-2 pb-1.5 border-b ${borderColor}`}>
              <PlayerIcon
                su={{ class_name: cls, class_emote_id: players[0].class_emote_id, spec_emote_id: null }}
                emoji={classEmoji.get(cls)}
              />
              <span className={`text-[10px] font-semibold tracking-wider ${headerColor}`}>
                {cls} ({players.length})
              </span>
              {isRemoved && (
                <span className="text-[9px] italic text-muted-foreground/60">{T.eventDetail.removedClass}</span>
              )}
            </div>
            <div className={isInf ? 'grid grid-cols-2 gap-1' : 'flex flex-col gap-1'}>
              {players.map(su => (
                <div key={su.user_id} className={cardClass}>
                  <Avatar su={su} px={18} />
                  <PlayerIcon su={su} emoji={classEmoji.get(su.class_name)} />
                  <span className={nameClass}>{su.name}</span>
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function EventDetailPanel({
  title, startTime, briefingTime, markUnset = false, description, eventId, signupBase = 'matches', isExternal = false, discordUrl,
  signUps, onConfigureSquads, configLabel = 'Configure Squads', onRefresh, onEditMatch,
  onRemoveMatch, removeLabel, mapName, teams, onSignUpChange, strategySlot,
}: Props) {
  const [confirmRemove, setConfirmRemove] = useState(false)
  const { user } = useAuth()
  const { T } = useLanguage()
  const [localSignUps, setLocalSignUps] = useState<SignUp[]>(signUps)
  const [classes, setClasses] = useState<RhClass[]>([])
  const [classesLoading, setClassesLoading] = useState(true)
  const [squadLayout, setSquadLayout] = useState<SquadData[] | null>(null)
  const [squadConfirmations, setSquadConfirmations] = useState<Record<string, string>>({})

  // Sync when parent updates sign-ups (after background refresh)
  useEffect(() => { setLocalSignUps(signUps) }, [signUps])

  // Fetched once here rather than inside the sign-up card: the class list also
  // decides the display order and icons of the roster below it.
  useEffect(() => {
    if (!eventId) return
    let cancelled = false
    const load = async () => {
      setClassesLoading(true)
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const r = await fetch(`/api/${signupBase}/${eventId}/classes`, { credentials: 'include' })
          if (cancelled) return
          if (r.ok) {
            const d = await r.json()
            if (!cancelled) { setClasses(d.classes ?? []); setClassesLoading(false) }
            return
          }
        } catch { /* network error, retry */ }
        if (cancelled) return
        if (attempt < 3) await new Promise(res => setTimeout(res, 600 * (attempt + 1)))
      }
      if (!cancelled) setClassesLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [eventId, signupBase])

  // Lookups derived from the class list; both fall back to the built-in tables
  // so Raid Helper events keep the ordering and icons they always had.
  const classRank  = new Map(classes.map((c, i) => [c.name, i]))
  const isLocalMatch = signupBase === 'matches' && !!eventId?.startsWith('tllm_')
  const classEmoji = new Map(classes.map(c => [c.name, c.emoji ?? '']))

  // Fetch squad layout + confirmations when a Discord post exists
  const fetchSquadData = useCallback(() => {
    if (!eventId) return
    const base = signupBase
    fetch(`/api/${base}/${eventId}/discord-post`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (!d.message_id) { setSquadLayout(null); return }
        // Use squad data from last_sent_json (what was actually posted to Discord)
        setSquadLayout(d.squads ?? [])
        fetch(`/api/${base}/${eventId}/confirmations`, { credentials: 'include' })
          .then(r => r.json())
          .then(confs => setSquadConfirmations(confs ?? {}))
          .catch(() => {})
      })
      .catch(() => {})
  }, [eventId, signupBase])

  useEffect(() => {
    fetchSquadData()
    const id = setInterval(fetchSquadData, 10_000)
    return () => clearInterval(id)
  }, [fetchSquadData])

  const handleLocalSignUpChange = (su: SignUp | null) => {
    setLocalSignUps(prev => {
      const without = prev.filter(s => s.user_id !== user?.id)
      return su ? [...without, su] : without
    })
    onSignUpChange?.(su)
  }

  const active    = localSignUps.filter(s => s.is_active && s.class_name !== 'Bench' && s.class_name !== 'Tentative')
  const bench     = localSignUps.filter(s => s.class_name === 'Bench')
  const tentative = localSignUps.filter(s => s.class_name === 'Tentative')

  const mySignUp = user ? localSignUps.find(s => s.user_id === user.id) : undefined

  // Group ALL sign-ups by class
  const classGroups: Record<string, SignUp[]> = {}
  for (const su of localSignUps) {
    if (!classGroups[su.class_name]) classGroups[su.class_name] = []
    classGroups[su.class_name].push(su)
  }
  const classOrder = Object.keys(classGroups).sort((a, b) => {
    const rank = (c: string) => classRank.get(c) ?? CLASS_PRIORITY[c] ?? 98
    return rank(a) - rank(b)
  })

  const pendingCount    = bench.length + tentative.length
  const parsedDescription = description ? parseDiscordTimestamps(description) : null

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-white/10 shrink-0">
        <div className="min-w-0">
          <h2 className="text-base font-bold leading-tight">{title}</h2>
          <div className="flex items-center gap-2 mt-0.5">
            {startTime && (briefingTime || markUnset) ? (
              <p className="text-xs text-muted-foreground">
                {formatDay(startTime)}
                {' · '}{T.matches.briefing}{' '}
                {briefingTime
                  ? <span className="text-foreground/80">{formatClock(briefingTime)}</span>
                  : <span className="italic text-muted-foreground/50">{T.matches.notSetYet}</span>}
                {' · '}{T.matches.kickoff} <span className="text-foreground/80">{formatClock(startTime)}</span>
              </p>
            ) : startTime ? <p className="text-xs text-muted-foreground">{formatDate(startTime)}</p> : null}
            {(mapName || markUnset) && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground/70">
                <MapIcon className="h-3 w-3" />
                {mapName || <span className="italic text-muted-foreground/50">{T.matches.notSetYet}</span>}
              </span>
            )}
          </div>
          {/* The sides, with the faction each plays when one was picked. The
              title usually names them too, but only this line says the faction. */}
          {teams && teams.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5">
              {teams.map((t, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  {i > 0 && <span className="text-[10px] text-muted-foreground/30 mr-0.5">vs</span>}
                  <FactionIcon faction={t.faction} className="h-3.5 w-3.5" />
                  <span className="text-xs text-muted-foreground/80">{t.name}</span>
                </span>
              ))}
            </div>
          )}
          {markUnset && !teams?.length && (
            <p className="mt-1.5 text-xs text-muted-foreground/70">
              {T.matches.formTeams}{' '}
              <span className="italic text-muted-foreground/50">{T.matches.notSetYet}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            <span className="text-foreground font-medium">{active.length}</span>
            {pendingCount > 0 && <span className="text-muted-foreground/60"> (+{pendingCount})</span>}
            <span className="text-xs"> {T.eventDetail.signedUpCount}</span>
          </span>
          {discordUrl && (
            <a
              href={discordUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium border border-violet-500/30 text-violet-400/80 bg-violet-500/8 hover:bg-violet-500/15 hover:text-violet-300 transition-all whitespace-nowrap"
            >
              <ExternalLink className="h-3 w-3" />
              {T.eventDetail.viewInDiscord}
            </a>
          )}
          {onConfigureSquads && (
            <Button size="sm" className="h-7 text-xs gap-1.5" onClick={onConfigureSquads}>
              {configLabel}
            </Button>
          )}
          {onEditMatch && (
            <button
              onClick={onEditMatch}
              className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground/40 hover:text-foreground hover:bg-white/8 transition-colors cursor-pointer"
              title={T.eventDetail.editMatchTitle}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {onRemoveMatch && (
            confirmRemove ? (
              <div className="flex items-center gap-1">
                {removeLabel && (
                  <span className="text-[11px] text-muted-foreground/70 max-w-[16rem]">{removeLabel}</span>
                )}
                <Button size="sm" variant="destructive" className="h-7 text-xs gap-1" onClick={() => { setConfirmRemove(false); onRemoveMatch() }}>
                  {T.eventDetail.confirmAction}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setConfirmRemove(false)}>
                  {T.eventDetail.cancelAction}
                </Button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmRemove(true)}
                className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors cursor-pointer"
                title={T.eventDetail.removeMatchTitle}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-5 space-y-4">

        {/* Description */}
        {parsedDescription && (
          <pre className="text-xs text-muted-foreground/80 leading-relaxed whitespace-pre-wrap font-sans bg-white/[0.035] border border-white/10 rounded-sm px-4 py-3">
            {parsedDescription}
          </pre>
        )}

        {/* Sign-up + Position cards side by side */}
        {eventId && (
          <div className="flex gap-3 flex-wrap items-start">
            {/* YOUR SIGN UP */}
            <div className="max-w-sm relative flex-1 min-w-[260px]">
              {!mySignUp && !isExternal && (
                <div className="absolute inset-0 rounded-sm border-2 border-primary/60 animate-pulse-fade pointer-events-none" />
              )}
              <div className="bg-white/[0.035] border border-white/10 rounded-sm px-4 py-3 space-y-3 h-full">
                <p className="text-base font-bold tracking-wide text-foreground">{T.eventDetail.yourSignUp}</p>
                {isExternal ? (
                  mySignUp ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2.5 px-3 py-2 rounded-sm bg-green-500/12 border border-green-500/25">
                        <div className="h-5 w-5 rounded-full bg-green-500/25 flex items-center justify-center shrink-0">
                          <Check className="h-3 w-3 text-green-400" />
                        </div>
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                          <PlayerIcon su={mySignUp} emoji={classEmoji.get(mySignUp.class_name)} />
                          <span className="text-sm font-bold text-foreground">{mySignUp.class_name}</span>
                        </div>
                      </div>
                      {discordUrl ? (
                        <a
                          href={discordUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium border border-red-500/25 text-red-400/70 bg-red-500/8 hover:bg-red-500/15 hover:text-red-400 transition-all"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {T.eventDetail.cancelSignUp}
                        </a>
                      ) : (
                        <p className="text-[10px] text-muted-foreground/40 italic">{T.eventDetail.externalModify}</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground/70">{T.eventDetail.notSignedUp}</p>
                      {discordUrl ? (
                        <a
                          href={discordUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium border border-violet-500/30 text-violet-400/80 bg-violet-500/8 hover:bg-violet-500/15 hover:text-violet-300 transition-all"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {T.eventDetail.signUp}
                        </a>
                      ) : (
                        <p className="text-xs text-muted-foreground/50 italic">
                          {T.eventDetail.externalNotAvailable}
                        </p>
                      )}
                    </div>
                  )
                ) : (
                  <SignUpSection
                    eventId={eventId}
                    signupBase={signupBase}
                    classes={classes}
                    classesLoading={classesLoading}
                    currentSignUp={mySignUp}
                    onLocalSignUpChange={handleLocalSignUpChange}
                    onRefresh={onRefresh}
                  />
                )}
              </div>
            </div>

            {/* YOUR POSITION */}
            <PositionCard
              eventId={eventId}
              signupBase={signupBase}
              squadLayout={squadLayout}
              confirmations={squadConfirmations}
              userId={user?.id}
              onConfirmChange={fetchSquadData}
            />
            {strategySlot}
          </div>
        )}

        {/* All classes side by side: active → bench → tentative → absence */}
        {classOrder.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold tracking-widest text-muted-foreground/60 mb-3">
              {T.eventDetail.signedUpSection.replace('{n}', String(active.length))}
              {pendingCount > 0 && <span className="text-muted-foreground/40"> (+{pendingCount})</span>}
            </p>
            <SignUpsByClass signUps={localSignUps} classes={classes} classesLoading={classesLoading} isLocalMatch={isLocalMatch} />
          </div>
        )}

        {/* Squad layout (read-only, shown when Discord post exists) */}
        {squadLayout && squadLayout.length > 0 && (
          <SquadDisplaySection
            squads={squadLayout}
            confirmations={squadConfirmations}
            signUps={localSignUps}
            layoutLabel={signupBase === 'events' ? T.eventDetail.squadLayout : T.eventDetail.rosterLayout}
          />
        )}

        {localSignUps.length === 0 && (
          <p className="text-sm text-muted-foreground/40 text-center py-12">{T.eventDetail.noSignUps}</p>
        )}
      </div>
    </div>
  )
}
